import React, { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A photo cropped to a square, or a quiet placeholder when there is no photo
 * or the browser can't show it — never the image's description in its place.
 * Size and corners come from `className`.
 */
export default function PhotoThumb({
  url,
  alt = '',
  className,
}: {
  url: string | undefined;
  /** Leave empty where the photo's text is already beside it. */
  alt?: string;
  className?: string;
}) {
  const [broken, setBroken] = useState<string | null>(null);
  const showing = url && broken !== url;
  return (
    <div className={cn('shrink-0 overflow-hidden rounded-2xl bg-muted', className)}>
      {showing ? (
        <img
          src={url}
          alt={alt}
          // A photo still on this page is already here; only the service's load lazily.
          loading={url.startsWith('blob:') ? undefined : 'lazy'}
          decoding="async"
          onError={() => setBroken(url)}
          className="size-full object-cover"
        />
      ) : (
        <div
          data-photo-placeholder
          role={alt ? 'img' : undefined}
          aria-label={alt || undefined}
          className="flex size-full items-center justify-center text-muted-foreground"
        >
          <ImageOff className="size-1/3 max-h-8 max-w-8" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
